<template>
  <select
    :id="id"
    :value="value"
    :disabled="disabled"
    :multiple="multiple"
    :class="selectClasses"
    :aria-invalid="ariaInvalid"
    :aria-describedby="ariaDescribedby"
    :aria-required="ariaRequired"
    v-bind="$attrs"
    @change="handleChange"
    @blur="$emit('blur', $event)"
    @focus="$emit('focus', $event)"
  >
    <option v-if="placeholder && !multiple" value="" disabled selected>
      {{ placeholder }}
    </option>
    <option
      v-for="(option, index) in normalizedOptions"
      :key="index"
      :value="option.value"
      :disabled="option.disabled"
    >
      {{ option.label }}
    </option>
  </select>
</template>

<script>
export default {
  name: 'ThemedSelect',
  inheritAttrs: false,
  props: {
    id: {
      type: String,
      default: null
    },
    value: {
      type: [String, Number, Array],
      default: ''
    },
    options: {
      type: Array,
      required: true,
      default: () => []
    },
    placeholder: {
      type: String,
      default: ''
    },
    disabled: {
      type: Boolean,
      default: false
    },
    error: {
      type: Boolean,
      default: false
    },
    multiple: {
      type: Boolean,
      default: false
    },
    ariaInvalid: {
      type: String,
      default: null
    },
    ariaDescribedby: {
      type: String,
      default: null
    },
    ariaRequired: {
      type: String,
      default: null
    }
  },
  computed: {
    selectClasses() {
      const baseClasses = 'themed-select flex h-9 w-full rounded-md border px-3 py-1 text-sm transition-all outline-none disabled:cursor-not-allowed disabled:opacity-50';
      
      const stateClasses = [];
      
      // Background and border
      stateClasses.push('bg-input-background border-input');
      
      // Focus states
      if (!this.error) {
        stateClasses.push('focus-visible:border-ring focus-visible:ring-2 focus-visible:ring-ring/50');
      }
      
      // Error state
      if (this.error) {
        stateClasses.push('border-destructive focus-visible:border-destructive focus-visible:ring-2 focus-visible:ring-destructive/20');
      }
      
      // Multiple select height adjustment
      if (this.multiple) {
        stateClasses.push('h-auto min-h-[2.25rem]');
      }
      
      return this.cn(baseClasses, ...stateClasses);
    },
    normalizedOptions() {
      // Support both simple arrays ['option1', 'option2'] 
      // and object arrays [{ label: 'Option 1', value: 'opt1' }]
      return this.options.map(option => {
        if (typeof option === 'string' || typeof option === 'number') {
          return {
            label: option,
            value: option,
            disabled: false
          };
        }
        return {
          label: option.label || option.text || option.name || option.value,
          value: option.value !== undefined ? option.value : option,
          disabled: option.disabled || false
        };
      });
    }
  },
  methods: {
    handleChange(event) {
      if (this.multiple) {
        // For multiple select, get all selected values
        const selectedOptions = Array.from(event.target.selectedOptions);
        const values = selectedOptions.map(option => this.coerceValue(option.value));
        this.$emit('input', values);
      } else {
        const value = event.target.value;
        this.$emit('input', this.coerceValue(value));
      }
    },
    coerceValue(value) {
      // Try to match the type of the original option value
      const matchingOption = this.normalizedOptions.find(opt => String(opt.value) === String(value));
      if (matchingOption) {
        return matchingOption.value;
      }
      return value;
    }
  }
}
</script>
